import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const HERO_IMG = "https://images.unsplash.com/photo-1762716514222-996318bb41a7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGdsb3dpbmclMjBuZW9uJTIwc3BoZXJlfGVufDB8fHx8MTc4NTg4ODQyNHww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate(location.state?.from?.pathname || "/", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-[#050505] overflow-hidden" data-testid="login-page">
      <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
      <div className="absolute inset-0 bg-black/60" />
      <div className="absolute inset-0 gold-glow" />

      <div className="relative z-10 min-h-screen flex flex-col justify-center px-6 py-16 max-w-md mx-auto w-full">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <p className="text-xs tracking-[0.5em] uppercase text-[#D4AF37] mb-4" data-testid="login-eyebrow">Holding Company</p>
          <h1 className="font-display text-4xl sm:text-5xl text-white leading-tight mb-2" data-testid="login-title">
            LIFE BY<br />DESIGN
          </h1>
          <p className="text-sm text-zinc-400 mb-10">One command centre. Six ventures. Zero excuses.</p>
        </motion.div>

        <motion.form
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="backdrop-blur-2xl bg-black/60 border border-white/10 rounded-md p-8 space-y-6"
          data-testid="login-form"
        >
          {error && (
            <div className="text-sm text-red-400 border border-red-500/30 bg-red-500/10 rounded-sm px-3 py-2" data-testid="login-error">
              {error}
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="email" className="text-xs uppercase tracking-widest text-zinc-400">Email</Label>
            <Input
              id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              autoCapitalize="none" autoCorrect="off" spellCheck={false}
              className="bg-white/5 border-white/10 text-white rounded-sm focus-visible:ring-[#D4AF37]"
              placeholder="you@lifebydesign.com" data-testid="login-email-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="text-xs uppercase tracking-widest text-zinc-400">Password</Label>
            <Input
              id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              autoCapitalize="none" autoCorrect="off" spellCheck={false}
              className="bg-white/5 border-white/10 text-white rounded-sm focus-visible:ring-[#D4AF37]"
              placeholder="••••••••" data-testid="login-password-input"
            />
          </div>
          <button
            type="submit" disabled={loading}
            className="w-full py-3 bg-[#D4AF37] text-black text-sm font-bold uppercase tracking-[0.2em] rounded-sm hover:bg-[#e5c555] transition-colors duration-200 disabled:opacity-50"
            data-testid="login-submit-button"
          >
            {loading ? "Entering…" : "Enter the Boardroom"}
          </button>
        </motion.form>
      </div>
    </div>
  );
}
